import {
  getSiteConfig,
  getPostSlugs,
  getWoocommerceProducts,
  getFooter,
} from '../../../lib/api';
import { useRouter } from 'next/router';
import NotFound from '../../404';
import Head from 'next/head';
import {
  localize,
  getValidLocale,
  postPageQuery,
  locales,
  useWindowSize,
  Layout,
} from '@lilbit/shared';
import { usePreviewSubscription, urlFor } from '../../../lib/sanity';
import { getClient } from '../../../lib/sanity.server';
import SimpleBlockContent from '../../../components/SimpleBlockContent';
import dynamic from 'next/dynamic';

const DesktopPost = dynamic(
  () => import('../../../../../shared/src/components/DesktopPost')
);
const MobilePost = dynamic(
  () => import('../../../../../shared/src/components/MobilePost')
);
const allowedCategories = [
  'blogg',
  'blog',
  'dogbreeds',
  'hunderaser',
  'news',
  'nyheter',
];

const Post = ({
  posts,
  config,
  route,
  localizedPosts,
  mainProduct,
  preview,
  slug,
  sanityLanguageIdentifier,
  locale,
  footer,
}) => {
  const router = useRouter();
  const size = useWindowSize();

  const additionalProps = {
    urlFor,
    SimpleBlockContent,
  };

  const { data: post } = usePreviewSubscription(postPageQuery, {
    params: {
      slug: slug ? slug : null,
      sanityLanguageIdentifier: sanityLanguageIdentifier
        ? sanityLanguageIdentifier
        : null,
      route: route ? route : null,
    },
    initialData: localizedPosts ? localizedPosts : null,
    enabled: preview,
  });

  if (router.isFallback) {
    return (
      <div className="flex justify-center items-center h-screen font-objektiv">
        Loading...
      </div>
    );
  }

  //Pass the simpleblockcontent component to the footer
  footer.SimpleBlockContent = SimpleBlockContent;

  if (
    (posts &&
      Object.keys(posts).length === 0 &&
      posts.constructor === Object) ||
    !posts ||
    !localizedPosts
  ) {
    if (config) {
      //404 with config for navbar
      return (
        <Layout
          footer={footer}
          config={config}
          title=""
          setMarginX={true}
          metadata=""
          imageMetadata={''}
          user=""
          urlFor={urlFor}
          siteName="lildog"
          mainProduct={mainProduct}
        >
          <Head>
            <meta />
          </Head>
          <NotFound locale={locale.urlPrefix} />
        </Layout>
      );
    }
    //If config is undefined
    return (
      <>
        <Head>
          <meta name="robots" />
        </Head>
        <NotFound locale={locale.urlPrefix} />
      </>
    );
  }
  const { category } = localizedPosts;

  const localizedPostsWithPreviewSub = localize(post, [
    sanityLanguageIdentifier,
  ]);
  //If post exists. Render it.
  return (
    <Layout
      footer={footer}
      config={config}
      title={`${post?.seoTitle ? post.seoTitle : post.title} | lildog`}
      setMarginX={false}
      metadata={post?.meta ? post?.meta : post?.title ? post?.title : ''}
      imageMetadata={
        urlFor(post?.mainImage, false).url()
          ? urlFor(post?.mainImage, false).url()
          : ''
      }
      user=""
      urlFor={urlFor}
      siteName="lildog"
      mainProduct={mainProduct}
    >
      {category === route && (
        <div>
          {size?.width < 1024 ? (
            <div className="lg:hidden">
              <MobilePost
                localizedPosts={localizedPostsWithPreviewSub}
                {...additionalProps}
              />
            </div>
          ) : (
            <div className="hidden max-w-7xl mx-auto lg:flex">
              <DesktopPost
                localizedPosts={localizedPostsWithPreviewSub}
                {...additionalProps}
              />
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

export const getStaticProps = async ({ params, preview = false }) => {
  const locale = getValidLocale(params?.lang);
  const sanityLanguageIdentifier = locale.sanityLanguageIdentifier;

  const slug = params?.post;
  const route = params?.category;
  const posts = await getClient(preview).fetch(postPageQuery, {
    sanityLanguageIdentifier,
    slug,
    route,
  });
  const localizedPosts = localize(posts, [sanityLanguageIdentifier]);
  const mainProduct = await getWoocommerceProducts(true);
  const config = await getSiteConfig(sanityLanguageIdentifier);
  const footerData = await getFooter();
  const footer = localize(footerData, [sanityLanguageIdentifier]);
  let slugsForAllLocales;
  if (posts && posts.slug) {
    slugsForAllLocales = posts.slug;
  }

  const slugsForAllCategories = posts ? posts.localeCategory : null;
  if (slugsForAllLocales && config) {
    config.slugsForAllLocales = slugsForAllLocales;
  }
  if (config) {
    config.slugsForAllCategories = slugsForAllCategories
      ? slugsForAllCategories
      : null;
  }
  return {
    props: {
      config,
      posts,
      route,
      localizedPosts,
      mainProduct,
      locale,
      preview,
      slug,
      footer,
      sanityLanguageIdentifier,
    },
    revalidate: 60 * 60 * 24,
  };
};

export const getStaticPaths = async () => {
  const slugsArray = await getPostSlugs();

  let paths = localizePaths(slugsArray, locales);
  if (process.env.ENVIRONMENT === 'DEV' || process.env.ENVIRONMENT === 'QA') {
    return {
      paths: [],
      fallback: true,
    };
  }
  return {
    paths: [],
    fallback: true,
  };
};

const localizePaths = (slugs, locales) => {
  var localizedPaths = [];
  for (let locale of locales) {
    for (let slugContainer of slugs) {
      const lang = locale.sanityLanguageIdentifier;
      if (!['en', 'no'].includes(lang)) continue; // Other languages are not translated so no point in trying to prerender them
      const post = slugContainer.slug[lang]?.current || null;
      const category = slugContainer.category[lang]?.current || null;
      if (post === null || category === null) continue; // This prevents posts that are not translated to be added to the paths
      localizedPaths.push({ params: { lang, category, post } });
    }
  }
  return localizedPaths;
};

export default Post;
